import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from './InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import { useRoles } from '../contexts/RolesContext';
import usePermissions from '../utils/usePermissions';
import LogoUpload from './LogoUpload';
import '../styles/AdminPanel.css';

const AdminPanel = ({ onLogoChange }) => {
  const currencyDefaults = {
    INR: 'Rs.',
    USD: '$',
    EUR: 'EUR ',
    GBP: 'GBP ',
    AED: 'AED '
  };

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showInlineNotice } = useInlineNotice();
  const { refreshRoles } = useRoles();
  const adminPermissions = usePermissions('admin');
  const [isEditing, setIsEditing] = useState(false);
  const [numberingSystem, setNumberingSystem] = useState('indian');
  const [currencyConfig, setCurrencyConfig] = useState({ code: 'INR', prefix: 'Rs.' });
  const [roundOffTotals, setRoundOffTotals] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    contactEmail: '',
    contactNumbers: [],
    websites: [],
    address: '',
    gst: '',
    bankAccounts: []
  });
  const [isSaving, setIsSaving] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState('logo');

  const [permissionsMatrix, setPermissionsMatrix] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [permissionsDirty, setPermissionsDirty] = useState(false);
  const [permissionsError, setPermissionsError] = useState('');
  
  // Counter reset state
  const [resetPrefix, setResetPrefix] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['Invoice', 'Quotation', 'Expense', 'Claim', 'Purchase']);

  const [lookupType, setLookupType] = useState('Invoice');
  const [lookupNumber, setLookupNumber] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [updatedCreatedAt, setUpdatedCreatedAt] = useState('');
  const [updatedUpdatedAt, setUpdatedUpdatedAt] = useState('');
  const [updateDateLoading, setUpdateDateLoading] = useState(false);
  const [updateDateSuccess, setUpdateDateSuccess] = useState('');
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupSchedulerUpdating, setBackupSchedulerUpdating] = useState(false);
  const [backupError, setBackupError] = useState('');

  const canManageLogo = adminPermissions.can('manageLogo');
  const canEditOrgDetails = adminPermissions.can('editOrgDetails');
  const canManageBanking = adminPermissions.can('manageBanking') || canEditOrgDetails;
  const canResetCounters = adminPermissions.can('resetCounters');
  const canManagePermissions = adminPermissions.can('managePermissions');
  const canManageDocumentDates = ['admin', 'appAdmin'].includes(adminPermissions.role);
  const canManageBackups = adminPermissions.role === 'appAdmin';

  // Fetch organization details on mount
  useEffect(() => {
    fetchOrganization();
  }, []);

  useEffect(() => {
    if (canManagePermissions) {
      fetchPermissions();
    } else {
      setPermissionsMatrix(null);
      setPermissionsDirty(false);
      setPermissionsError('');
    }
  }, [canManagePermissions]);

  useEffect(() => {
    if (canManageBackups) {
      fetchBackupStatus();
    } else {
      setBackupStatus(null);
      setBackupError('');
    }
  }, [canManageBackups]);

  const buildBankAccounts = (data) => {
    if (Array.isArray(data?.bankAccounts) && data.bankAccounts.length > 0) {
      return data.bankAccounts.map((account, index) => ({
        bankName: account.bankName || '',
        accountNumber: account.accountNumber || '',
        ifscCode: account.ifscCode || '',
        type: account.type === 'Secondary' ? 'Secondary' : index === 0 ? 'Primary' : 'Secondary'
      }));
    }

    if (data?.bankName || data?.accountNumber || data?.ifscCode) {
      return [
        {
          bankName: data.bankName || '',
          accountNumber: data.accountNumber || '',
          ifscCode: data.ifscCode || '',
          type: 'Primary'
        }
      ];
    }

    return [];
  };

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/organisations/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOrganization(response.data);
      const contactNumbers = Array.isArray(response.data?.contactNumbers)
        ? response.data.contactNumbers.map((value) => String(value || '').trim()).filter(Boolean)
        : (response.data?.phone ? [String(response.data.phone).trim()] : []);
      const websites = Array.isArray(response.data?.websites)
        ? response.data.websites.map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      setFormData({
        name: response.data.name || '',
        contactName: response.data.contactName || '',
        contactEmail: response.data.contactEmail || '',
        contactNumbers,
        websites,
        address: response.data.address || '',
        gst: response.data.gst || '',
        bankAccounts: buildBankAccounts(response.data)
      });
      setNumberingSystem(response.data?.metadata?.numberingSystem || 'indian');
      setCurrencyConfig({
        code: response.data?.metadata?.currency?.code || 'INR',
        prefix: response.data?.metadata?.currency?.prefix || 'Rs.'
      });
      setRoundOffTotals(Boolean(response.data?.metadata?.roundOffTotals));
      setResetPrefix(response.data?.metadata?.documentPrefix || '');
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactNumberChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.contactNumbers];
      updated[index] = value;
      return { ...prev, contactNumbers: updated };
    });
  };

  const handleAddContactNumber = () => {
    setFormData((prev) => ({
      ...prev,
      contactNumbers: [...prev.contactNumbers, '']
    }));
  };

  const handleRemoveContactNumber = (index) => {
    setFormData((prev) => ({
      ...prev,
      contactNumbers: prev.contactNumbers.filter((_, idx) => idx !== index)
    }));
  };

  const handleWebsiteChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.websites];
      updated[index] = value;
      return { ...prev, websites: updated };
    });
  };

  const handleAddWebsite = () => {
    setFormData((prev) => ({
      ...prev,
      websites: [...prev.websites, '']
    }));
  };

  const handleRemoveWebsite = (index) => {
    setFormData((prev) => ({
      ...prev,
      websites: prev.websites.filter((_, idx) => idx !== index)
    }));
  };

  const handleBankAccountChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.bankAccounts];
      updated[index] = {
        ...updated[index],
        [field]: value
      };

      if (field === 'type' && value === 'Primary') {
        return {
          ...prev,
          bankAccounts: updated.map((account, idx) => ({
            ...account,
            type: idx === index ? 'Primary' : 'Secondary'
          }))
        };
      }

      return {
        ...prev,
        bankAccounts: updated
      };
    });
  };

  const handleAddBankAccount = () => {
    setFormData(prev => ({
      ...prev,
      bankAccounts: [
        ...prev.bankAccounts,
        {
          bankName: '',
          accountNumber: '',
          ifscCode: '',
          type: prev.bankAccounts.length === 0 ? 'Primary' : 'Secondary'
        }
      ]
    }));
  };

  const handleRemoveBankAccount = (index) => {
    setFormData(prev => {
      const updated = prev.bankAccounts.filter((_, idx) => idx !== index);
      if (updated.length > 0 && !updated.some((account) => account.type === 'Primary')) {
        updated[0] = { ...updated[0], type: 'Primary' };
      }
      return {
        ...prev,
        bankAccounts: updated
      };
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');

      if (!formData.name.trim()) {
        setError('Organization name is required');
        return;
      }

      if (formData.bankAccounts.length > 0) {
        const primaryCount = formData.bankAccounts.filter((account) => account.type === 'Primary').length;
        if (primaryCount !== 1) {
          setError('Please select exactly one primary bank account');
          return;
        }
      }

      const token = localStorage.getItem('token');
      const cleanedContactNumbers = formData.contactNumbers
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      const cleanedWebsites = formData.websites
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      const payload = {
        ...formData,
        contactNumbers: cleanedContactNumbers,
        websites: cleanedWebsites,
        metadata: {
          ...(organization?.metadata || {}),
          numberingSystem,
          currency: currencyConfig,
          roundOffTotals
        }
      };
      const response = await axios.put(
        `${API_BASE_URL}/organisations/current`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setOrganization(response.data);
      if (response.data?.metadata?.numberingSystem) {
        localStorage.setItem('numberingSystem', response.data.metadata.numberingSystem);
        setNumberingSystem(response.data.metadata.numberingSystem);
      }
      if (response.data?.metadata?.currency) {
        localStorage.setItem('currencyCode', response.data.metadata.currency.code);
        localStorage.setItem('currencyPrefix', response.data.metadata.currency.prefix);
        setCurrencyConfig(response.data.metadata.currency);
      }
      localStorage.setItem('roundOffTotals', response.data?.metadata?.roundOffTotals ? 'true' : 'false');
      setRoundOffTotals(Boolean(response.data?.metadata?.roundOffTotals));
      setIsEditing(false);
      showInlineNotice('Settings updated successfully.', { scope: 'admin' });
    } catch (err) {
      console.error('Error updating organization:', err);
      setError(err.response?.data?.error || 'Failed to update organization details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    const contactNumbers = Array.isArray(organization?.contactNumbers)
      ? organization.contactNumbers.map((value) => String(value || '').trim()).filter(Boolean)
      : (organization?.phone ? [String(organization.phone).trim()] : []);
    const websites = Array.isArray(organization?.websites)
      ? organization.websites.map((value) => String(value || '').trim()).filter(Boolean)
      : [];
    setFormData({
      name: organization?.name || '',
      contactName: organization?.contactName || '',
      contactEmail: organization?.contactEmail || '',
      contactNumbers,
      websites,
      address: organization?.address || '',
      gst: organization?.gst || '',
      bankAccounts: buildBankAccounts(organization)
    });
    setNumberingSystem(organization?.metadata?.numberingSystem || 'indian');
    setCurrencyConfig({
      code: organization?.metadata?.currency?.code || 'INR',
      prefix: organization?.metadata?.currency?.prefix || 'Rs.'
    });
    setRoundOffTotals(Boolean(organization?.metadata?.roundOffTotals));
  };

  const toggleAccordion = (section) => {
    setExpandedAccordion(expandedAccordion === section ? null : section);
  };

  const formatPermissionLabel = (value) => {
    if (!value) return '';
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatApiAccess = (pathValue) => {
    if (!pathValue) return 'Access';
    const verbPart = String(pathValue).trim().split(' ')[0];
    if (!verbPart) return 'Access';

    const verbMap = {
      GET: 'Read',
      POST: 'Create',
      PUT: 'Update',
      PATCH: 'Update',
      DELETE: 'Delete'
    };

    const actions = verbPart
      .split('/')
      .map((verb) => verbMap[verb])
      .filter(Boolean);

    if (actions.length === 0) return 'Access';
    return actions.join(' / ');
  };

  const getRoleList = () => {
    const roles = permissionsMatrix?.meta?.roles || Object.keys(permissionsMatrix?.roles || {});
    return roles.filter((roleKey) => roleKey !== 'guest');
  };

  const getPageKeys = (roles) => {
    const keys = new Set();
    roles.forEach((roleKey) => {
      const pages = permissionsMatrix?.roles?.[roleKey]?.pages || {};
      Object.keys(pages).forEach((pageKey) => keys.add(pageKey));
    });
    return Array.from(keys);
  };

  const getActionKeysForPage = (roles, pageKey) => {
    const keys = new Set();
    roles.forEach((roleKey) => {
      const actions = permissionsMatrix?.roles?.[roleKey]?.pages?.[pageKey]?.actions || {};
      Object.keys(actions).forEach((actionKey) => keys.add(actionKey));
    });
    return Array.from(keys);
  };

  const getApiKeys = (roles) => {
    const keys = new Set();
    roles.forEach((roleKey) => {
      const apis = permissionsMatrix?.roles?.[roleKey]?.api || {};
      Object.keys(apis).forEach((apiKey) => keys.add(apiKey));
    });
    return Array.from(keys);
  };

  const getApiAccessLabel = (roles, apiKey) => {
    for (const roleKey of roles) {
      const apiConfig = permissionsMatrix?.roles?.[roleKey]?.api?.[apiKey];
      if (apiConfig?.path) {
        return formatApiAccess(apiConfig.path);
      }
    }
    return 'Access';
  };

  const getFlagValue = (flag) => {
    if (!flag) return false;
    if (typeof flag === 'boolean') return flag;
    return flag.intended ?? flag.current ?? false;
  };

  const updatePermissionsMatrix = (updateFn) => {
    setPermissionsMatrix((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      updateFn(next);
      return next;
    });
    setPermissionsDirty(true);
  };

  const setPageViewPermission = (roleKey, pageKey, value) => {
    updatePermissionsMatrix((next) => {
      const page = next.roles?.[roleKey]?.pages?.[pageKey];
      if (!page) return;
      const view = page.view && typeof page.view === 'object'
        ? page.view
        : { current: Boolean(page.view), intended: Boolean(page.view) };
      view.intended = value;
      page.view = view;
    });
  };

  const setPageActionPermission = (roleKey, pageKey, actionKey, value) => {
    updatePermissionsMatrix((next) => {
      const action = next.roles?.[roleKey]?.pages?.[pageKey]?.actions?.[actionKey];
      if (action === undefined) return;
      const target = action && typeof action === 'object'
        ? action
        : { current: Boolean(action), intended: Boolean(action) };
      target.intended = value;
      next.roles[roleKey].pages[pageKey].actions[actionKey] = target;
    });
  };

  const setApiPermission = (roleKey, apiKey, value) => {
    updatePermissionsMatrix((next) => {
      const apiConfig = next.roles?.[roleKey]?.api?.[apiKey];
      if (!apiConfig) return;
      const allow = apiConfig.allow && typeof apiConfig.allow === 'object'
        ? apiConfig.allow
        : { current: Boolean(apiConfig.allow ?? apiConfig), intended: Boolean(apiConfig.allow ?? apiConfig) };
      allow.intended = value;
      apiConfig.allow = allow;
    });
  };

  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      setPermissionsError('');
      const response = await axios.get(`${API_BASE_URL}/permissions`);
      const matrix = response.data?.rolesMatrix || response.data?.permissions || response.data;
      setPermissionsMatrix(matrix);
      setPermissionsDirty(false);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setPermissionsError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handlePermissionsSave = async () => {
    if (!permissionsMatrix) return;
    try {
      setPermissionsSaving(true);
      setPermissionsError('');
      const response = await axios.put(`${API_BASE_URL}/permissions`, {
        rolesMatrix: permissionsMatrix
      });
      const matrix = response.data?.rolesMatrix || permissionsMatrix;
      setPermissionsMatrix(matrix);
      setPermissionsDirty(false);
      showInlineNotice('Permissions updated successfully.', { scope: 'admin' });
      refreshRoles();
    } catch (err) {
      console.error('Error saving permissions:', err);
      const errorMsg = err.response?.data?.error || 'Failed to update permissions';
      setPermissionsError(errorMsg);
    } finally {
      setPermissionsSaving(false);
    }
  };

  const handleResetCounters = async () => {
    const normalizedPrefix = resetPrefix.trim();
    if (!window.confirm(`Are you sure you want to reset document counters to prefix ${normalizedPrefix}? This will affect all future documents for the selected types.`)) {
      return;
    }

    try {
      setIsResetting(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/organisations/reset-counters`,
        {
          documentPrefix: normalizedPrefix,
          types: selectedTypes
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const summary = response.data?.results?.map(r => `${r.type}-${r.prefix || r.year || ''}`.trim()).filter(Boolean).join(', ');
      showInlineNotice(`Counters reset to prefix ${normalizedPrefix}. ${summary || ''}`.trim(), { scope: 'admin' });
    } catch (err) {
      console.error('Error resetting counters:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reset counters';
      setError(errorMsg);
    } finally {
      setIsResetting(false);
    }
  };

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const formatDateTimeForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const tzOffset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - tzOffset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const parseInputDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  const handleDocumentLookup = async () => {
    setLookupError('');
    setLookupResult(null);
    setUpdateDateSuccess('');

    if (!lookupType || !lookupNumber.trim()) {
      setLookupError('Document type and number are required.');
      return;
    }

    try {
      setLookupLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/documents/lookup`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            type: lookupType,
            documentNumber: lookupNumber.trim()
          }
        }
      );

      const document = response.data?.document;
      setLookupResult(document || null);
      setUpdatedCreatedAt(formatDateTimeForInput(document?.createdAt));
      setUpdatedUpdatedAt(formatDateTimeForInput(document?.updatedAt));
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to find document.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleUpdateDocumentDates = async () => {
    setLookupError('');
    setUpdateDateSuccess('');

    if (!lookupResult) {
      setLookupError('Please lookup a document before updating dates.');
      return;
    }

    const createdAtPayload = parseInputDateTime(updatedCreatedAt);
    const updatedAtPayload = parseInputDateTime(updatedUpdatedAt);

    if (!createdAtPayload || !updatedAtPayload) {
      setLookupError('Please use valid date and time values for both fields.');
      return;
    }

    try {
      setUpdateDateLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/documents/${lookupResult._id}/dates`,
        {
          createdAt: createdAtPayload,
          updatedAt: updatedAtPayload
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const document = response.data?.document;
      setLookupResult(document || null);
      setUpdatedCreatedAt(formatDateTimeForInput(document?.createdAt));
      setUpdatedUpdatedAt(formatDateTimeForInput(document?.updatedAt));
      setUpdateDateSuccess('Document dates updated successfully.');
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to update document dates.');
    } finally {
      setUpdateDateLoading(false);
    }
  };

  const fetchBackupStatus = async () => {
    try {
      setBackupLoading(true);
      setBackupError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/backups/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackupStatus(response.data);
    } catch (err) {
      setBackupError(err.response?.data?.error || 'Failed to load backup status.');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRunBackupNow = async () => {
    if (!window.confirm('Run database backup now? This will dump MongoDB and upload it to S3.')) {
      return;
    }

    try {
      setBackupRunning(true);
      setBackupError('');

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/backups/run`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBackupStatus((prev) => ({
        ...(prev || {}),
        lastRun: response.data,
        running: false,
        runningSince: null
      }));

      showInlineNotice('Database backup completed successfully.', { scope: 'admin' });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to run backup.';
      setBackupError(msg);
    } finally {
      setBackupRunning(false);
      fetchBackupStatus();
    }
  };

  const handleDownloadLatestBackup = async () => {
    try {
      setBackupDownloading(true);
      setBackupError('');

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/backups/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        },
        params: { _ts: Date.now() }
      });

      const url = response.data?.url;
      if (!url) {
        throw new Error('Backup download URL was not returned');
      }

      const popup = window.open(url, '_blank', 'noopener,noreferrer');
      if (!popup) {
        window.location.assign(url);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to generate backup download URL.';
      setBackupError(msg);
    } finally {
      setBackupDownloading(false);
    }
  };

  const handleToggleBackupScheduler = async () => {
    const currentlyEnabled = Boolean(backupStatus?.scheduler?.enabled);

    try {
      setBackupSchedulerUpdating(true);
      setBackupError('');

      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/backups/scheduler`,
        { enabled: !currentlyEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBackupStatus((prev) => ({
        ...(prev || {}),
        scheduler: response.data?.scheduler || { enabled: !currentlyEnabled }
      }));

      showInlineNotice(
        `Backup scheduler ${currentlyEnabled ? 'disabled' : 'enabled'} successfully.`,
        { scope: 'admin' }
      );
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update backup scheduler state.';
      setBackupError(msg);
    } finally {
      setBackupSchedulerUpdating(false);
      fetchBackupStatus();
    }
  };

  const formatBackupDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-section">
        <div className="admin-header">
          <h1>Admin Settings</h1>
        </div>
        <div className="admin-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const displayBankAccounts = buildBankAccounts(organization);
  const permissionsRoleList = getRoleList();
  const permissionsPageKeys = getPageKeys(permissionsRoleList);
  const permissionsApiKeys = getApiKeys(permissionsRoleList);

  return (
    <div className="admin-section">
      <div className="admin-header">
        <h1>Admin Settings</h1>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}
      <InlineNotice scope="admin" />
      
      <div className="admin-content">
        <div className="accordion">
          {/* Logo Accordion Item */}
          {canManageLogo && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'logo' ? 'active' : ''}`}
              onClick={() => toggleAccordion('logo')}
            >
              <span className="accordion-title">Company Logo</span>
              <span className="accordion-icon">
                {expandedAccordion === 'logo' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'logo' && (
              <div className="accordion-body">
                <LogoUpload onLogoChange={onLogoChange} />
              </div>
            )}
          </div>
          )}

          {/* Organization Details Accordion Item */}
          {canEditOrgDetails && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'org' ? 'active' : ''}`}
              onClick={() => toggleAccordion('org')}
            >
              <span className="accordion-title">Organization Details</span>
              <span className="accordion-icon">
                {expandedAccordion === 'org' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'org' && (
              <div className="accordion-body">
                <div className="organization-settings">
                  {!isEditing ? (
                    <div className="org-details-view">
                      <div className="detail-row">
                        <label>Organization Name:</label>
                        <span>{organization?.name || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Contact Name:</label>
                        <span>{organization?.contactName || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Contact Email:</label>
                        <span>{organization?.contactEmail || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Contact Numbers:</label>
                        <span>
                          {Array.isArray(organization?.contactNumbers) && organization.contactNumbers.length > 0
                            ? organization.contactNumbers.join(', ')
                            : (organization?.phone || 'N/A')}
                        </span>
                      </div>
                      <div className="detail-row">
                        <label>Websites:</label>
                        <span>
                          {Array.isArray(organization?.websites) && organization.websites.length > 0
                            ? organization.websites.join(', ')
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <label>Address:</label>
                        <span>{organization?.address || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>GST Number:</label>
                        <span>{organization?.gst || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Numbering System:</label>
                        <span>{(organization?.metadata?.numberingSystem || 'indian') === 'international' ? 'International' : 'Indian'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Currency:</label>
                        <span>{organization?.metadata?.currency?.code || 'INR'} ({organization?.metadata?.currency?.prefix || 'Rs.'})</span>
                      </div>
                      <div className="detail-row">
                        <label>Round Off Totals:</label>
                        <span>{organization?.metadata?.roundOffTotals ? 'Enabled' : 'Disabled'}</span>
                      </div>
                      <button 
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Details
                      </button>
                    </div>
                  ) : (
                    <div className="org-details-form">
                      <div className="form-group">
                        <label>Organization Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter organization name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Contact Name</label>
                        <input
                          type="text"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter contact name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Contact Email</label>
                        <input
                          type="email"
                          name="contactEmail"
                          value={formData.contactEmail}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter contact email"
                        />
                      </div>
                      <div className="form-group">
                        <label>Contact Numbers</label>
                        {formData.contactNumbers.length > 0 ? (
                          formData.contactNumbers.map((value, index) => (
                            <div key={`contact-number-${index}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <input
                                type="tel"
                                value={value}
                                onChange={(e) => handleContactNumberChange(index, e.target.value)}
                                className="form-control"
                                placeholder="Enter contact number"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveContactNumber(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '8px' }}>
                            No contact numbers added.
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleAddContactNumber}
                        >
                          Add Contact Number
                        </button>
                      </div>
                      <div className="form-group">
                        <label>Websites</label>
                        {formData.websites.length > 0 ? (
                          formData.websites.map((value, index) => (
                            <div key={`website-${index}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => handleWebsiteChange(index, e.target.value)}
                                className="form-control"
                                placeholder="Enter website"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveWebsite(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '8px' }}>
                            No websites added.
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleAddWebsite}
                        >
                          Add Website
                        </button>
                      </div>
                      <div className="form-group">
                        <label>Address</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="form-control"
                          rows="3"
                          placeholder="Enter organization address"
                        />
                      </div>
                      <div className="form-group">
                        <label>GST Number</label>
                        <input
                          type="text"
                          name="gst"
                          value={formData.gst}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter GST number"
                        />
                      </div>
                      <div className="form-group">
                        <label>Numbering System</label>
                        <select
                          value={numberingSystem}
                          onChange={(e) => setNumberingSystem(e.target.value)}
                          className="form-control"
                        >
                          <option value="indian">Indian (thousands, lakhs, crores)</option>
                          <option value="international">International (thousands, millions, billions)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Currency</label>
                        <select
                          value={currencyConfig.code}
                          onChange={(e) => {
                            const code = e.target.value;
                            setCurrencyConfig({
                              code,
                              prefix: currencyDefaults[code] || currencyConfig.prefix
                            });
                          }}
                          className="form-control"
                        >
                          <option value="INR">INR (Indian Rupee)</option>
                          <option value="USD">USD (US Dollar)</option>
                          <option value="EUR">EUR (Euro)</option>
                          <option value="GBP">GBP (British Pound)</option>
                          <option value="AED">AED (UAE Dirham)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Currency Prefix</label>
                        <input
                          type="text"
                          value={currencyConfig.prefix}
                          onChange={(e) => setCurrencyConfig((prev) => ({ ...prev, prefix: e.target.value }))}
                          className="form-control"
                          placeholder="Enter currency prefix"
                        />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 0 }}>
                          <input
                            type="checkbox"
                            checked={roundOffTotals}
                            onChange={(e) => setRoundOffTotals(e.target.checked)}
                          />
                          <span>Round Off Totals</span>
                        </label>
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn btn-success"
                          onClick={handleSave}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleCancel}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Banking Details Accordion Item */}
          {canManageBanking && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'banking' ? 'active' : ''}`}
              onClick={() => toggleAccordion('banking')}
            >
              <span className="accordion-title">Banking Details</span>
              <span className="accordion-icon">
                {expandedAccordion === 'banking' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'banking' && (
              <div className="accordion-body">
                <div className="organization-settings">
                  {!isEditing ? (
                    <div className="org-details-view">
                      {displayBankAccounts.length > 0 ? (
                        <div className="bank-accounts-table-wrapper">
                          <table className="bank-accounts-table">
                            <thead>
                              <tr>
                                <th>Bank Name</th>
                                <th>Account Number</th>
                                <th>IFSC Code</th>
                                <th>Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayBankAccounts.map((account, index) => (
                                <tr key={`${account.bankName}-${index}`}>
                                  <td>{account.bankName || 'N/A'}</td>
                                  <td>{account.accountNumber || 'N/A'}</td>
                                  <td>{account.ifscCode || 'N/A'}</td>
                                  <td>{account.type || 'Primary'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="empty-bank-accounts">No bank accounts configured.</div>
                      )}
                      <button
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Details
                      </button>
                    </div>
                  ) : (
                    <div className="org-details-form">
                      <div className="bank-accounts-table-wrapper">
                        <table className="bank-accounts-table">
                          <thead>
                            <tr>
                              <th>Bank Name</th>
                              <th>Account Number</th>
                              <th>IFSC Code</th>
                              <th>Type</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.bankAccounts.length > 0 ? (
                              formData.bankAccounts.map((account, index) => (
                                <tr key={`bank-account-${index}`}>
                                  <td>
                                    <input
                                      type="text"
                                      value={account.bankName}
                                      onChange={(e) => handleBankAccountChange(index, 'bankName', e.target.value)}
                                      className="form-control"
                                      placeholder="Enter bank name"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={account.accountNumber}
                                      onChange={(e) => handleBankAccountChange(index, 'accountNumber', e.target.value)}
                                      className="form-control"
                                      placeholder="Enter account number"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={account.ifscCode}
                                      onChange={(e) => handleBankAccountChange(index, 'ifscCode', e.target.value)}
                                      className="form-control"
                                      placeholder="Enter IFSC code"
                                    />
                                  </td>
                                  <td>
                                    <select
                                      value={account.type}
                                      onChange={(e) => handleBankAccountChange(index, 'type', e.target.value)}
                                      className="form-control"
                                    >
                                      <option value="Primary">Primary</option>
                                      <option value="Secondary">Secondary</option>
                                    </select>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRemoveBankAccount(index)}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="empty-bank-accounts">
                                  No bank accounts added yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="bank-accounts-actions">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleAddBankAccount}
                        >
                          Add Bank Account
                        </button>
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn btn-success"
                          onClick={handleSave}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleCancel}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Reset Document Numbering Accordion Item */}
          {canResetCounters && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'reset' ? 'active' : ''}`}
              onClick={() => toggleAccordion('reset')}
            >
              <span className="accordion-title">Reset Document Numbering</span>
              <span className="accordion-icon">
                {expandedAccordion === 'reset' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'reset' && (
              <div className="accordion-body">
                <div className="reset-counter-section">
                  <p className="section-description">
                    Reset document numbering for a new prefix. This will reset the sequence numbers for selected document types.
                  </p>
                  
                  <div className="example-box">
                    <strong>Examples:</strong>
                    <div>Prefix: IND/2024-25</div>
                    <div>Generated: QT-IND/2024-25/001, INV-IND/2024-25/001, EXP-IND/2024-25/001</div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="resetPrefix">Document Prefix for Numbering</label>
                    <input
                      type="text"
                      id="resetPrefix"
                      className="form-control"
                      value={resetPrefix}
                      onChange={(e) => setResetPrefix(e.target.value)}
                      placeholder="Enter prefix (e.g., IND/2024-25)"
                    />
                    <small className="form-text text-muted">
                      Enter the prefix for new documents (accepts alphanumeric characters and symbols)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Document Types to Reset</label>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes('Quotation')}
                          onChange={() => handleTypeToggle('Quotation')}
                        />
                        <span>Quotations (QT-)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes('Invoice')}
                          onChange={() => handleTypeToggle('Invoice')}
                        />
                        <span>Invoices (INV-)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes('Expense')}
                          onChange={() => handleTypeToggle('Expense')}
                        />
                        <span>Expenses (EXP-)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes('Claim')}
                          onChange={() => handleTypeToggle('Claim')}
                        />
                        <span>Claims (CLM-)</span>
                      </label>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes('Purchase')}
                          onChange={() => handleTypeToggle('Purchase')}
                        />
                        <span>Purchases (PUR-)</span>
                      </label>
                    </div>
                  </div>

                  <div className="warning-box">
                    <strong>⚠️ Warning:</strong> This action will reset the document numbering sequence. 
                    All new documents of selected types will start from {(resetPrefix || 'PREFIX').trim()}/001. 
                    This action cannot be undone.
                  </div>

                  <button
                    className="btn btn-danger"
                    onClick={handleResetCounters}
                    disabled={isResetting || selectedTypes.length === 0 || !resetPrefix.trim()}
                  >
                    {isResetting ? 'Resetting...' : 'Reset'}
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {canManageBackups && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'backups' ? 'active' : ''}`}
              onClick={() => toggleAccordion('backups')}
            >
              <span className="accordion-title">Database Backups</span>
              <span className="accordion-icon">
                {expandedAccordion === 'backups' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'backups' && (
              <div className="accordion-body">
                <div className="backup-section">
                  <p className="section-description">
                    Scheduled backups run every Sunday at 7:00 AM and are saved in your backup bucket under the db-backup subfolder.
                  </p>

                  {backupError && <div className="alert alert-danger">{backupError}</div>}

                  <div className="backup-actions">
                    <button
                      className={`btn ${backupStatus?.scheduler?.enabled ? 'btn-warning' : 'btn-success'}`}
                      onClick={handleToggleBackupScheduler}
                      disabled={backupLoading || backupRunning || backupDownloading || backupSchedulerUpdating}
                    >
                      {backupSchedulerUpdating
                        ? 'Updating Scheduler...'
                        : (backupStatus?.scheduler?.enabled ? 'Disable Scheduler' : 'Enable Scheduler')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleRunBackupNow}
                      disabled={backupRunning || backupLoading || backupStatus?.running || backupDownloading || backupSchedulerUpdating}
                    >
                      {backupRunning || backupStatus?.running ? 'Backup Running...' : 'Run Backup Now'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleDownloadLatestBackup}
                      disabled={backupLoading || backupRunning || backupStatus?.running || backupDownloading || backupSchedulerUpdating}
                    >
                      {backupDownloading ? 'Preparing Download...' : 'Download Last Backup'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={fetchBackupStatus}
                      disabled={backupLoading || backupRunning || backupDownloading || backupSchedulerUpdating}
                    >
                      {backupLoading ? 'Refreshing...' : 'Refresh Status'}
                    </button>
                  </div>

                  <div className="backup-status-grid">
                    <div className="detail-row">
                      <label>Current Status:</label>
                      <span>{backupStatus?.running ? 'Running' : 'Idle'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Scheduler:</label>
                      <span>{backupStatus?.scheduler?.enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Schedule:</label>
                      <span>{backupStatus?.scheduler?.cron || '0 7 * * 0'} ({backupStatus?.scheduler?.timezone || 'Asia/Kolkata'})</span>
                    </div>
                    <div className="detail-row">
                      <label>Backup Bucket:</label>
                      <span>{backupStatus?.bucket || 'Not configured'}</span>
                    </div>
                    <div className="detail-row">
                      <label>S3 Subfolder:</label>
                      <span>{backupStatus?.prefix || 'db-backup'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Retention Days:</label>
                      <span>{backupStatus?.retentionDays || 30}</span>
                    </div>
                    <div className="detail-row">
                      <label>Last Backup:</label>
                      <span>{formatBackupDate(backupStatus?.lastRun?.finishedAt)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Last Result:</label>
                      <span>
                        {backupStatus?.lastRun
                          ? (backupStatus.lastRun.success ? 'Success' : `Failed: ${backupStatus.lastRun.error || 'Unknown error'}`)
                          : 'No backup run yet'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Last Object Key:</label>
                      <span>{backupStatus?.lastRun?.key || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {canManageDocumentDates && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'documentDates' ? 'active' : ''}`}
              onClick={() => toggleAccordion('documentDates')}
            >
              <span className="accordion-title">Update Document Dates</span>
              <span className="accordion-icon">
                {expandedAccordion === 'documentDates' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'documentDates' && (
              <div className="accordion-body">
                <div className="document-dates-panel">
                  <p className="section-description">
                    Lookup a document by type and number, then edit its created and updated timestamps.
                  </p>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1, marginRight: '12px' }}>
                      <label>Document Type</label>
                      <select
                        value={lookupType}
                        onChange={(e) => setLookupType(e.target.value)}
                        className="form-control"
                      >
                        <option value="Invoice">Invoice</option>
                        <option value="Quotation">Quotation</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 2 }}>
                      <label>Document Number</label>
                      <input
                        type="text"
                        value={lookupNumber}
                        onChange={(e) => setLookupNumber(e.target.value)}
                        className="form-control"
                        placeholder="Enter full document number"
                      />
                    </div>
                  </div>

                  <div className="form-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleDocumentLookup}
                      disabled={lookupLoading}
                    >
                      {lookupLoading ? 'Looking up...' : 'Lookup Document'}
                    </button>
                  </div>

                  {lookupError && (
                    <div className="alert alert-danger" style={{ marginTop: '12px' }}>
                      {lookupError}
                    </div>
                  )}

                  {lookupResult && (
                    <div className="lookup-result" style={{ marginTop: '18px' }}>
                      <div className="detail-row">
                        <label>Document ID:</label>
                        <span>{lookupResult._id}</span>
                      </div>
                      <div className="detail-row">
                        <label>Type:</label>
                        <span>{lookupResult.type}</span>
                      </div>
                      <div className="detail-row">
                        <label>Number:</label>
                        <span>{lookupResult.documentNumber}</span>
                      </div>
                      <div className="form-row" style={{ gap: '12px', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ flex: 1, minWidth: '240px' }}>
                          <label>Created At</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            value={updatedCreatedAt}
                            onChange={(e) => setUpdatedCreatedAt(e.target.value)}
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1, minWidth: '240px' }}>
                          <label>Updated At</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            value={updatedUpdatedAt}
                            onChange={(e) => setUpdatedUpdatedAt(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="form-actions" style={{ marginTop: '12px' }}>
                        <button
                          className="btn btn-success"
                          onClick={handleUpdateDocumentDates}
                          disabled={updateDateLoading}
                        >
                          {updateDateLoading ? 'Updating...' : 'Update Dates'}
                        </button>
                      </div>

                      {updateDateSuccess && (
                        <div className="alert alert-success" style={{ marginTop: '12px' }}>
                          {updateDateSuccess}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Permissions Accordion Item */}
          {canManagePermissions && (
          <div className="accordion-item">
            <button
              className={`accordion-header ${expandedAccordion === 'permissions' ? 'active' : ''}`}
              onClick={() => toggleAccordion('permissions')}
            >
              <span className="accordion-title">Permissions</span>
              <span className="accordion-icon">
                {expandedAccordion === 'permissions' ? '−' : '+'}
              </span>
            </button>
            {expandedAccordion === 'permissions' && (
              <div className="accordion-body">
                <div className="permissions-panel">
                  <div className="permissions-header">
                    <div className="permissions-toolbar">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={fetchPermissions}
                        disabled={permissionsLoading || permissionsSaving}
                      >
                        Reload
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={handlePermissionsSave}
                        disabled={permissionsSaving || permissionsLoading || !permissionsDirty}
                      >
                        {permissionsSaving ? 'Saving...' : 'Save Permissions'}
                      </button>
                    </div>
                  </div>

                  {permissionsError && (
                    <div className="alert alert-danger">{permissionsError}</div>
                  )}

                  {permissionsLoading && (
                    <div className="permissions-loading">Loading permissions...</div>
                  )}

                  {!permissionsLoading && permissionsMatrix && permissionsRoleList.length > 0 && (
                    <div className="permissions-content">
                      <div className="permissions-section">
                        <h3>Pages</h3>
                        {permissionsRoleList.length === 0 && (
                          <div className="permissions-empty">No roles available.</div>
                        )}
                        {permissionsRoleList.length > 0 && (
                          <div
                            className="permissions-matrix"
                            style={{
                              gridTemplateColumns: `minmax(180px, 1.2fr) repeat(${permissionsRoleList.length}, minmax(90px, 1fr))`
                            }}
                          >
                            <div className="permissions-matrix-row permissions-matrix-header">
                              <div className="permissions-matrix-cell permissions-matrix-label">Page / Action</div>
                              {permissionsRoleList.map((roleKey) => (
                                <div className="permissions-matrix-cell permissions-matrix-role" key={`role-${roleKey}`}>
                                  {formatPermissionLabel(roleKey)}
                                </div>
                              ))}
                            </div>
                            {permissionsPageKeys.map((pageKey) => {
                              const actionKeys = getActionKeysForPage(permissionsRoleList, pageKey);
                              return (
                                <React.Fragment key={`page-${pageKey}`}>
                                  <div className="permissions-matrix-row permissions-matrix-section">
                                    <div
                                      className="permissions-matrix-cell permissions-matrix-section-title"
                                      style={{ gridColumn: `1 / span ${permissionsRoleList.length + 1}` }}
                                    >
                                      {formatPermissionLabel(pageKey)}
                                    </div>
                                  </div>
                                  <div className="permissions-matrix-row">
                                    <div className="permissions-matrix-cell permissions-matrix-label">View</div>
                                    {permissionsRoleList.map((roleKey) => {
                                      const pageConfig = permissionsMatrix.roles?.[roleKey]?.pages?.[pageKey];
                                      const checked = getFlagValue(pageConfig?.view);
                                      return (
                                        <div className="permissions-matrix-cell" key={`view-${pageKey}-${roleKey}`}>
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => setPageViewPermission(roleKey, pageKey, e.target.checked)}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {actionKeys.map((actionKey) => (
                                    <div className="permissions-matrix-row" key={`action-${pageKey}-${actionKey}`}>
                                      <div className="permissions-matrix-cell permissions-matrix-label">
                                        {formatPermissionLabel(actionKey)}
                                      </div>
                                      {permissionsRoleList.map((roleKey) => {
                                        const actionConfig = permissionsMatrix.roles?.[roleKey]?.pages?.[pageKey]?.actions?.[actionKey];
                                        const checked = getFlagValue(actionConfig);
                                        const disabled = actionConfig === undefined;
                                        return (
                                          <div className="permissions-matrix-cell" key={`action-${pageKey}-${actionKey}-${roleKey}`}>
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              disabled={disabled}
                                              onChange={(e) => setPageActionPermission(roleKey, pageKey, actionKey, e.target.checked)}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="permissions-section">
                        <h3>API Access</h3>
                        {permissionsApiKeys.length === 0 && (
                          <div className="permissions-empty">No API permissions available.</div>
                        )}
                        {permissionsApiKeys.length > 0 && (
                          <div
                            className="permissions-matrix permissions-matrix-api"
                            style={{
                              gridTemplateColumns: `minmax(200px, 1.4fr) repeat(${permissionsRoleList.length}, minmax(90px, 1fr))`
                            }}
                          >
                            <div className="permissions-matrix-row permissions-matrix-header">
                              <div className="permissions-matrix-cell permissions-matrix-label">API Permission</div>
                              {permissionsRoleList.map((roleKey) => (
                                <div className="permissions-matrix-cell permissions-matrix-role" key={`api-role-${roleKey}`}>
                                  {formatPermissionLabel(roleKey)}
                                </div>
                              ))}
                            </div>
                            {permissionsApiKeys.map((apiKey) => (
                              <div className="permissions-matrix-row" key={`api-${apiKey}`}>
                                <div className="permissions-matrix-cell permissions-matrix-label">
                                  <div className="permissions-matrix-title">{formatPermissionLabel(apiKey)}</div>
                                  <div className="permissions-matrix-subtitle">
                                    {getApiAccessLabel(permissionsRoleList, apiKey)}
                                  </div>
                                </div>
                                {permissionsRoleList.map((roleKey) => {
                                  const apiConfig = permissionsMatrix.roles?.[roleKey]?.api?.[apiKey];
                                  const checked = getFlagValue(apiConfig?.allow);
                                  const disabled = !apiConfig;
                                  return (
                                    <div className="permissions-matrix-cell" key={`api-${apiKey}-${roleKey}`}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={(e) => setApiPermission(roleKey, apiKey, e.target.checked)}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;